import { useEffect, useState } from "react";
import { Users, Banknote, TrendingUp, Megaphone, MessageSquare, Crown, Activity, Award, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import "../admin-affiliate-premium.css";

function Stat({ label, value, sub, icon: Icon, tone = "gold", trend }) {
  const tones = {
    gold: { c: "#f4e9a1", b: "rgba(212,175,55,0.45)", g: "rgba(212,175,55,0.18)" },
    emerald: { c: "#6ee7b7", b: "rgba(16,185,129,0.45)", g: "rgba(16,185,129,0.18)" },
    amber: { c: "#fcd34d", b: "rgba(244,180,49,0.45)", g: "rgba(244,180,49,0.18)" },
    blue: { c: "#93c5fd", b: "rgba(59,130,246,0.45)", g: "rgba(59,130,246,0.18)" },
    rose: { c: "#fda5b1", b: "rgba(229,75,89,0.45)", g: "rgba(229,75,89,0.18)" },
    slate: { c: "#cbd5e1", b: "rgba(148,163,184,0.32)", g: "rgba(148,163,184,0.10)" },
  };
  const t = tones[tone] || tones.gold;
  return (
    <div className="ap-glass ap-card-hover" style={{ padding: 18, background: `linear-gradient(135deg, ${t.g}, transparent 70%)`, border: `1px solid ${t.b}` }}>
      <div className="flex items-start justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/55">{label}</div>
        {Icon && <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(0,0,0,0.35)", border: `1px solid ${t.b}`, color: t.c }}><Icon size={16} /></div>}
      </div>
      <div className="text-white font-black tracking-tight" style={{ fontSize: 24 }}>{value}</div>
      <div className="flex items-center gap-2 mt-1">
        {trend != null && (
          <span className={`text-[10.5px] font-bold inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md`} style={{ color: trend >= 0 ? "#6ee7b7" : "#fda5b1", background: trend >= 0 ? "rgba(16,185,129,0.12)" : "rgba(229,75,89,0.12)", border: `1px solid ${trend >= 0 ? "rgba(16,185,129,0.35)" : "rgba(229,75,89,0.35)"}` }}>
            {trend >= 0 ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />} {Math.abs(trend)}%
          </span>
        )}
        {sub && <div className="text-[11px] text-white/45">{sub}</div>}
      </div>
    </div>
  );
}

export default function AffiliateOverview() {
  const [data, setData] = useState(null);
  const load = async () => {
    try { const r = await api.get("/admin/aff-overview"); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="ap-bg p-8 text-white/55">Loading…</div>;
  return (
    <div className="ap-bg p-4 lg:p-6">
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-amber-300 font-bold mb-1">EXECUTIVE OVERVIEW</div>
          <div className="text-3xl font-black ap-gold-text leading-tight">Affiliate Command Center</div>
          <div className="text-white/55 text-sm mt-1">Live KPIs, top performers, and pipeline at a glance.</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Stat label="Active Affiliates" value={data.active_accounts} sub={`${data.total_accounts} total · ${data.disabled_accounts} disabled`} icon={Users} tone="gold" />
        <Stat label="New (30d)" value={data.new_accounts_30d} sub={`+${data.new_accounts_7d} in last 7d`} icon={TrendingUp} tone="emerald" trend={data.mom_growth_pct} />
        <Stat label="Total Paid Out" value={`৳${(data.total_paid || 0).toLocaleString()}`} sub="Lifetime affiliate payouts" icon={Banknote} tone="amber" />
        <Stat label="Pending Payouts" value={data.pending_payouts_count} sub={`৳${(data.pending_payouts_total || 0).toLocaleString()} queued`} icon={Activity} tone="rose" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Clicks (30d)" value={data.clicks_30d.toLocaleString()} icon={Megaphone} tone="blue" />
        <Stat label="Conversions (30d)" value={data.conversions_30d.toLocaleString()} icon={Award} tone="emerald" sub={`${data.conversion_rate_30d_pct}% CTR`} />
        <Stat label="Open Tickets" value={data.open_tickets} icon={MessageSquare} tone="rose" />
        <Stat label="MoM Growth" value={`${data.mom_growth_pct}%`} icon={TrendingUp} tone="gold" trend={data.mom_growth_pct} />
      </div>

      <div className="ap-glass overflow-hidden">
        <div className="flex items-center justify-between p-4" style={{ borderBottom: "1px solid rgba(212,175,55,0.20)" }}>
          <div className="font-black text-white text-lg">Top Affiliates by Lifetime Payout</div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300 font-bold">leaderboard</div>
        </div>
        <div className="ap-table overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Affiliate</th>
                <th>Lifetime Paid</th>
              </tr>
            </thead>
            <tbody>
              {(data.top_affiliates || []).length === 0 ? (
                <tr><td colSpan={3} className="text-center text-white/45 py-5">No data yet.</td></tr>
              ) : data.top_affiliates.map((t, i) => (
                <tr key={t.account_id}>
                  <td><Crown size={14} className={i === 0 ? "text-amber-300" : "text-white/40"} /> #{i + 1}</td>
                  <td className="font-bold text-white">{t.username}</td>
                  <td className="font-black text-amber-100">৳{t.lifetime_paid.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
